import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, Filter, Download, ChevronLeft, ChevronRight, 
  Eye, CheckCircle, XCircle, Clock, Users, Store, 
  TrendingUp, AlertCircle, MapPin, Phone, Mail, 
  Star, Grid3x3, List, ShoppingBag, DollarSign, Calendar
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSellers, approveSeller, rejectSeller } from '../../redux/adminUsersSlice';
import { useNavigate } from 'react-router-dom';

const Sellers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sellers, loading, error } = useSelector((state) => state.adminUsers);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [gridView, setGridView] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    dispatch(fetchSellers());
  }, [dispatch]);

  // Derived stats
  const stats = {
    total: sellers.length,
    pending: sellers.filter(s => s.sellerStatus === 'pending').length,
    approved: sellers.filter(s => s.sellerStatus === 'approved').length,
    rejected: sellers.filter(s => s.sellerStatus === 'rejected').length,
    totalRevenue: sellers.reduce((sum, s) => sum + (s.revenue || 0), 0),
    totalProducts: sellers.reduce((sum, s) => sum + (s.productCount || 0), 0)
  };

  const filteredSellers = sellers.filter(seller => {
    const matchesSearch = seller.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          seller.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          seller.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || seller.sellerStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredSellers.length / itemsPerPage);
  const paginatedSellers = filteredSellers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved': 
        return { label: 'Approved', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'pending': 
        return { label: 'Pending', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'rejected': 
        return { label: 'Rejected', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: 
        return { label: status || 'Unknown', icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const handleApprove = (id) => {
    if (!window.confirm('Approve this seller?')) return;
    dispatch(approveSeller(id));
  };

  const handleReject = (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    dispatch(rejectSeller({ id, reason }));
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const SellerCard = ({ seller }) => {
    const statusConfig = getStatusConfig(seller.sellerStatus);
    const StatusIcon = statusConfig.icon;
    
    return (
      <div className="seller-card">
        <div className="seller-card-cover">
          <div className="cover-overlay"></div>
          <div className="seller-logo">
            <span>{seller.storeName?.charAt(0).toUpperCase() || seller.username?.charAt(0).toUpperCase() || 'S'}</span>
          </div>
          <div className="seller-status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
            <StatusIcon size={12} />
            {statusConfig.label}
          </div>
        </div>
        
        <div className="seller-card-content">
          <h5 className="seller-name">{seller.storeName || seller.username}</h5>
          <div className="seller-rating">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />
            ))}
            <span className="rating-value">4.8</span>
            <span className="rating-count">(128)</span>
          </div>
          
          <div className="seller-details">
            <div className="detail-item">
              <Mail size={14} />
              <span>{seller.email}</span>
            </div>
            <div className="detail-item">
              <Phone size={14} />
              <span>{seller.phone || 'Not provided'}</span>
            </div>
            <div className="detail-item">
              <Calendar size={14} />
              <span>Joined {new Date(seller.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
          
          <div className="seller-stats">
            <div className="stat">
              <ShoppingBag size={14} />
              <span>{seller.productCount || 0} Products</span>
            </div>
            <div className="stat">
              <DollarSign size={14} />
              <span>₹{(seller.revenue || 0).toLocaleString()}</span>
            </div>
          </div>
          
          <div className="seller-actions">
            <button className="action-btn view" onClick={() => navigate(`/admin/sellers/details/${seller._id}`)}>
              <Eye size={16} />
              View Details
            </button>
            {seller.sellerStatus === 'pending' && (
              <>
                <button className="action-btn approve" onClick={() => handleApprove(seller._id)}>
                  <CheckCircle size={16} />
                  Approve
                </button>
                <button className="action-btn reject" onClick={() => handleReject(seller._id)}>
                  <XCircle size={16} />
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading sellers...</p>
      </div>
    );
  }

  return (
    <div className="admin-sellers-modern">
      {/* Header */}
      <div className="sellers-header">
        <div>
          <h1 className="page-title">Manage Sellers</h1>
          <p className="page-subtitle">View and manage all sellers on your platform</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Data
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Sellers" value={stats.total} icon={Store} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Pending Approval" value={stats.pending} icon={Clock} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Approved" value={stats.approved} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Total Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} icon={TrendingUp} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by name, store, or email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <div className="view-toggle">
            <button className={`view-btn ${gridView ? 'active' : ''}`} onClick={() => setGridView(true)}>
              <Grid3x3 size={16} />
            </button>
            <button className={`view-btn ${!gridView ? 'active' : ''}`} onClick={() => setGridView(false)}>
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredSellers.length} Sellers Found</span>
      </div>

      {/* Sellers Grid */}
      {gridView ? (
        <div className="sellers-grid">
          {paginatedSellers.map((seller) => (
            <SellerCard key={seller._id} seller={seller} />
          ))}
        </div>
      ) : (
        <div className="table-container">
          <div className="table-responsive">
            <table className="sellers-table">
              <thead>
                <tr>
                  <th>Store / Seller</th>
                  <th>Email</th>
                  <th>Products</th>
                  <th>Revenue</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSellers.map((seller) => {
                  const statusConfig = getStatusConfig(seller.sellerStatus);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={seller._id}>
                      <td className="seller-cell">
                        <div className="seller-avatar">
                          <span>{seller.storeName?.charAt(0).toUpperCase() || seller.username?.charAt(0).toUpperCase() || 'S'}</span>
                        </div>
                        <div className="seller-info">
                          <div className="seller-name">{seller.storeName || seller.username}</div>
                          <div className="seller-username">@{seller.username}</div>
                        </div>
                      </td>
                      <td className="email-cell">{seller.email}</td>
                      <td className="products-cell">{seller.productCount || 0}</td>
                      <td className="revenue-cell">₹{(seller.revenue || 0).toLocaleString()}</td>
                      <td className="status-cell">
                        <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                          <StatusIcon size={12} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="date-cell">
                        {new Date(seller.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="text-right">
                        <div className="action-buttons">
                          <button className="action-btn view" onClick={() => navigate(`/admin/sellers/details/${seller._id}`)}>
                            <Eye size={16} />
                          </button>
                          {seller.sellerStatus === 'pending' && (
                            <>
                              <button className="action-btn approve" onClick={() => handleApprove(seller._id)}>
                                <CheckCircle size={16} />
                              </button>
                              <button className="action-btn reject" onClick={() => handleReject(seller._id)}>
                                <XCircle size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredSellers.length)} of {filteredSellers.length} sellers
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Sellers;